import React from "react"
import { usePageProps } from "react-rb"
import { addDays, format } from "date-fns"

const discountedPeriod = ({ plan, promoCode }) => {
  if (plan.interval == "month") {
    return `each of the first ${promoCode.duration_in_months} months`
  } else {
    return `the first year`
  }
}

const BillingNoticeWithPromo = ({ plan, trialing, trialPeriodEnd }) => {
  const promoCode = plan.promo_code

  const when = trialing ? trialPeriodEnd : plan.billing_day
  const whenDisplay = trialing ? (
    <>
      starting at the end of your free trial on{" "}
      <span className="font-semibold">{when}</span>
    </>
  ) : (
    <>
      starting on <span className="font-semibold">{when}</span>
    </>
  )

  return (
    <>
      You will be billed <span className="font-semibold">{promoCode.text}</span>{" "}
      (plus applicable tax) for {discountedPeriod({ plan, promoCode })},{" "}
      {whenDisplay}. Then <span className="font-semibold">{plan.text}</span>{" "}
      (plus applicable tax) on{" "}
      <span className="font-semibold">{plan.recurring_billing_day}</span> each
      following {plan.interval}.
    </>
  )
}

const StandardBillingNotice = ({ plan, trialing, trialPeriodEnd }) => {
  const amount = plan.text

  const when = trialing ? trialPeriodEnd : plan.billing_day

  const recurrance = plan.interval == "month" ? `of each month` : `each year`

  const whenDisplay = trialing ? (
    <>
      at the end of your free trial on{" "}
      <span className="font-semibold">{when}</span>
    </>
  ) : (
    <>
      on <span className="font-semibold">{when}</span>
    </>
  )

  return (
    <>
      You will be billed <span className="font-semibold">{amount}</span> (plus
      applicable tax) {whenDisplay} and again on {plan.recurring_billing_day}{" "}
      {recurrance}.
    </>
  )
}

// Creators on a free plan can be in a lot of different states.
// They may or may not have added a card and been set up in Stripe.
// They may or may not have had a regular billing cycle when they were previously on a paid plan.
// They may or may not have a credit on their account.
//
// For these reasons, showing a detailed billing message is really difficult. So, I'm giving them
// a simpler one
const FreePlanBillingNotice = ({ plan, trialPeriodEnd }) => {
  const { freeAccessLevel } = usePageProps()

  if (freeAccessLevel) {
    let trialPeriodEndDisplay = trialPeriodEnd
    // If the account has not used their trial,
    // And the back-end has not given us a trial end date,
    // We can calculate a trial end date based on the plan's trial period days.
    if (
      !plan.account_trial_ended &&
      !trialPeriodEnd &&
      Number(plan.trial_period_days) > 0
    ) {
      const trialPeriodEndDate = addDays(new Date(), plan.trial_period_days)
      trialPeriodEndDisplay = format(trialPeriodEndDate, "MMMM d, yyyy")
    }

    const when =
      !plan.account_trial_ended && trialPeriodEndDisplay
        ? ` at the end of your free trial on ${trialPeriodEndDisplay}`
        : ""

    if (plan.promo_code?.valid) {
      return (
        <>
          You will be billed{" "}
          <span className="font-semibold">{plan.promo_code.text}</span> (plus
          applicable tax){when}.
        </>
      )
    } else {
      return (
        <>
          You will be billed <span className="font-semibold">{plan.text}</span>{" "}
          (plus applicable tax){when}.
        </>
      )
    }
  }

  if (plan.promo_code?.valid) {
    return (
      <>
        You will be billed{" "}
        <span className="font-semibold">{plan.promo_code.text}</span> (plus
        applicable tax).
      </>
    )
  } else {
    return (
      <>
        You will be billed <span className="font-semibold">{plan.text}</span>{" "}
        (plus applicable tax).
      </>
    )
  }
}

const BillingNotice = props => {
  if (props.currentlyOnFreePlan) {
    return <FreePlanBillingNotice {...props} />
  } else if (props.plan.promo_code?.valid) {
    return <BillingNoticeWithPromo {...props} />
  } else {
    return <StandardBillingNotice {...props} />
  }
}

export default BillingNotice
